import { useEffect, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";

import { trpc } from "./trpc";
import { cachedQuery } from "./cached-query";
import { setStoredSelection } from "./selected-vertical-store";

// Single source of truth for "which vertical/jurisdiction is the
// consumer looking at right now?". Selection rule:
//
//   1. URL params (?vertical=oupv&jurisdiction=FEDERAL) win — supports
//      deep links and lets a multi-entitled user switch without state
//   2. Otherwise, the user's most-recent ACTIVE entitlement (already
//      ordered by createdAt desc on the server)
//   3. If the user has no entitlements, returns null verticalSlug —
//      callers should render a discovery / signup view instead of
//      attempting to query questions
//
// Pages that render practice / mastery / review / etc. read from this
// hook; they no longer hardcode a slug. The home page renders all
// entitlements and lets the user click into one (which sets URL params
// and re-renders).

export type Entitlement = {
  purchaseId: string;
  verticalSlug: string;
  verticalName: string;
  jurisdictionCode: string | null;
  jurisdictionName: string | null;
  sku: string;
  isSubscription: boolean;
  createdAt: string;
};

export interface SelectedVertical {
  // null means "user has no entitlement and no URL param" — caller
  // should show the discovery view, not call questions.list.
  verticalSlug: string | null;
  jurisdictionCode: string | null;
  // The full entitlement list, for rendering pickers / cards.
  entitlements: Entitlement[];
  // True until the entitlement list has been fetched at least once OR
  // an error has been surfaced. Pages should render a spinner during
  // this window.
  isLoading: boolean;
  // True when the entitlement fetch failed. Distinct from an empty
  // entitlement list — a fetch failure must NOT be read as "user has
  // nothing" (which would strand a paid user on /welcome).
  loadError: boolean;
  // Navigate to /<path>?vertical=X&jurisdiction=Y. Used by entitlement
  // cards on home and (eventually) a header switcher.
  switchTo: (e: {
    verticalSlug: string;
    jurisdictionCode: string | null;
  }) => void;
}

// Friendly "Vertical · Jurisdiction" label for the active selection,
// resolved from the matching entitlement's display names (falling back to
// the raw slug). Shared by the paid-surface scaffolds (home appbar +
// mastery/review sidebar chip) so they all read the same.
export function verticalDisplayLabel(
  s: Pick<
    SelectedVertical,
    "entitlements" | "verticalSlug" | "jurisdictionCode"
  >,
): string | undefined {
  const current = s.entitlements.find(
    (e) =>
      e.verticalSlug === s.verticalSlug &&
      (e.jurisdictionCode ?? null) === s.jurisdictionCode,
  );
  if (current) {
    return `${current.verticalName}${
      current.jurisdictionName ? ` · ${current.jurisdictionName}` : ""
    }`;
  }
  return s.verticalSlug ?? undefined;
}

export function useSelectedVertical(): SelectedVertical {
  const params = useLocalSearchParams<{
    vertical?: string;
    jurisdiction?: string;
  }>();
  const router = useRouter();

  const [entitlements, setEntitlements] = useState<Entitlement[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Retry once on failure — a transient network blip shouldn't
      // surface as a load error and route a paid user to /welcome.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const res = await cachedQuery("entitlement.list", () =>
            trpc.entitlement.list.query(),
          );
          if (cancelled) return;
          // tRPC deserializes Date → string over the wire; coerce
          // defensively in case the runtime gives us either.
          setEntitlements(
            res.map((e) => ({ ...e, createdAt: String(e.createdAt) })),
          );
          setLoadError(false);
          return;
        } catch {
          if (cancelled) return;
          if (attempt === 1) {
            // Keep `entitlements` null (unknown), NOT [] — downstream
            // reads [] as "user has nothing." Surface the error instead.
            setLoadError(true);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const urlSlug = typeof params.vertical === "string" ? params.vertical : null;
  const urlJur =
    typeof params.jurisdiction === "string" ? params.jurisdiction : null;

  // First entitlement = most recent (server orders by createdAt desc).
  const fallback = entitlements?.[0] ?? null;

  const verticalSlug = urlSlug ?? fallback?.verticalSlug ?? null;
  const jurisdictionCode =
    // If the URL pinned a vertical, also honor its jurisdiction param
    // (don't accidentally inherit a different entitlement's jurisdiction).
    urlSlug
      ? urlJur
      : (fallback?.jurisdictionCode ?? null);

  // Persist the active URL-driven selection so it survives an auth
  // round-trip that strips the query params (see selected-vertical-store).
  // Only the URL form is recorded — an entitlement-derived fallback is
  // already durable on the server, and recording it would risk a stale
  // free pick overriding a real entitlement after sign-in.
  useEffect(() => {
    if (urlSlug) {
      setStoredSelection({ verticalSlug: urlSlug, jurisdictionCode: urlJur });
    }
  }, [urlSlug, urlJur]);

  return {
    verticalSlug,
    jurisdictionCode,
    entitlements: entitlements ?? [],
    // Loading until the first success OR a surfaced error. Once
    // loadError is true we stop spinning and let callers branch on it.
    isLoading: entitlements === null && !loadError,
    loadError,
    switchTo: ({ verticalSlug: v, jurisdictionCode: j }) => {
      router.setParams({
        vertical: v,
        ...(j ? { jurisdiction: j } : { jurisdiction: undefined }),
      });
    },
  };
}
